const FORM_ELEMENT_TYPES = Object.freeze([
	"FULL_NAME",
	"EMAIL_ADDRESS",
	"MULTILINE",
	"TEXT",
	"SCALE",
	"DATE",
	"CHECKBOXES",
	"RADIOBUTTONS",
	"DROP_DOWN",
	"TITLE",
	"FOOTER",
	"SCORE_RESULT",
	"SCORE_BUCKET_RESULT",
	"ADDRESS",
	"PHONE_NUMBER",
	"DATE_OF_BIRTH",
	"EMERGENCY_CONTACTS",
	"CONTACT_INFO",
	"TEXT_GRID",
	"CHOICE",
])
const FORM_NON_ANSWER_TYPES = Object.freeze(["TITLE", "FOOTER", "SCORE_RESULT", "SCORE_BUCKET_RESULT"])

export function getFormConfigErrors(config = {}) {
	let formElements = config.fields || config.formElements || []
	if (!Array.isArray(formElements) || (!formElements.length && !config.fixedPromptSequence && !config.allowEmptyFields)) {
		return ["Invalid tool configuration"]
	}
	for (let formElement of formElements) {
		const errors = getFormElementErrors(formElement)
		if (errors.length) return errors
		if (formElement.showWhen && !formElements.some(field => field.id === formElement.showWhen.fieldId && field.type === "SCALE")) return ["Invalid condition rating question"]
		if ((formElement.specialValues || []).some(option => option.requiresFieldId && !formElements.some(field => field.id === option.requiresFieldId && ["TEXT", "MULTILINE"].includes(field.type)))) return ["Invalid explanation question"]
	}
	return []
}

export function getFormElementErrors(formElement = {}) {
	if (!formElement.id || !formElement.type || !FORM_ELEMENT_TYPES.includes(formElement.type)) {
		return ["Invalid form element configuration"]
	}
	if (!FORM_NON_ANSWER_TYPES.includes(formElement.type) && !formElement.title) {
		return ["Invalid form question configuration"]
	}
	if (formElement.type === "CHOICE") {
		const choices = formElement.choices || []
		if (!Array.isArray(choices) || choices.some(choice => !choice.id || !choice.title)) return ["Invalid choice configuration"]
	}
	if (["RADIOBUTTONS", "CHECKBOXES", "DROP_DOWN"].includes(formElement.type)) {
		return getMultiChoiceElementErrors(formElement)
	}
	if (formElement.type === "SCALE" && (formElement.min != null || formElement.max != null)) {
		const min = Number(formElement.min ?? 0)
		const max = Number(formElement.max ?? 10)
		if (!Number.isInteger(min) || !Number.isInteger(max) || ![0, 1].includes(min) || max < 3 || max > 10 || min >= max) return ["Invalid rating range"]
		if (formElement.answerOptions !== undefined) {
			if (!Array.isArray(formElement.answerOptions)) return ["Invalid rating choices"]
			const ids = new Set()
			for (const option of formElement.answerOptions) {
				if (!option.id || !String(option.title || "").trim() || ids.has(option.id) || !Number.isFinite(Number(option.value)) || Number(option.value) < min || Number(option.value) > max) return ["Invalid rating choices"]
				ids.add(option.id)
			}
		}
	}
	return []
}

export function getMultiChoiceElementErrors(formElement = {}) {
	let options = formElement.answerOptions || []
	if (!Array.isArray(options) || options.length < 2) {
		return ["Invalid choice configuration"]
	}
	return []
}

export function getRequiredFormAnswerErrors(payload = {}, config = {}) {
	let answers = payload.answers || {}
	let requiredElements = (config.fields || config.formElements || []).filter(formElement => formElement.isRequired)
	for (let formElement of requiredElements) {
		if (isFormAnswerMissing(answers[formElement.id])) {
			return ["Required answer is missing"]
		}
	}
	return []
}

export function isFormAnswerMissing(answer) {
	let result = answer === "" || answer == null
	if (Array.isArray(answer)) {
		result = answer.length === 0
	}
	return result
}

export function getFormCompletion(config = {}, answers = {}) {
	const fields = config.fields || config.formElements || []
	const required = fields.filter(field => field.isCompletionRequired)
	const missing = required.filter(field => isFormAnswerMissing(answers[field.id]) || (typeof answers[field.id] === "string" && !answers[field.id].trim()))
	const missingSpecialExplanations = fields.flatMap(field => {
		const selected = (field.specialValues || []).find(option => option.id === answers[field.id])
		return selected?.requiresFieldId && isFormAnswerMissing(answers[selected.requiresFieldId]) ? [selected.requiresFieldId] : []
	})
	const hasAnswer = fields.some(field => !isFormAnswerMissing(answers[field.id]) && (typeof answers[field.id] !== "string" || !!answers[field.id].trim()))
	const minimumGroups = (config.minimumGroups || []).map(group => {
		const groupFields = Array.isArray(group.fieldIds) ? fields.filter(field => group.fieldIds.includes(field.id)) : fields.filter(field => field.id.startsWith(group.prefix) && field.id.endsWith(group.suffix))
		const answered = groupFields.filter(field => !isFormAnswerMissing(answers[field.id]) && String(answers[field.id]).trim()).length
		return {id: group.id, title: group.title, answered, minimum: Math.min(Number(group.minimum) || 0, groupFields.length)}
	})
	const incompleteGroups = minimumGroups.filter(group => group.answered < group.minimum)
	const completion = {isComplete: fields.length > 0 && missing.length === 0 && !missingSpecialExplanations.length && hasAnswer && !incompleteGroups.length, missingFieldIds: [...missing.map(field => field.id), ...missingSpecialExplanations]}
	return Array.isArray(config.minimumGroups) ? {...completion, minimumGroups, incompleteGroups} : completion
}

export function getStructuredFormView(config = {}, answers = {}) {
	const fields = config.fields || []
	const sections = (config.sections || []).map(section => ({...section, fields: []}))
	if (!sections.length) sections.push({id: "default", title: config.title || "Form", fields: []})
	for (const field of fields) {
		const section = sections.find(item => item.id === field.section) || sections[0]
		const answer = answers[field.id]
		const special = (field.specialValues || []).find(option => option.id === answer)
		const choice = (field.choices || []).find(option => option.id === answer)
		const numericAnchor = field.type === "SCALE" && Number.isInteger(answer) ? (field.anchors || [])[answer - Number(field.min || 0)] : ""
		let displayAnswer = isFormAnswerMissing(answer) ? "Unresolved" : `${answer}${numericAnchor ? ` — ${numericAnchor}` : ""}`
		if (special) displayAnswer = special.title
		else if (choice) displayAnswer = choice.title
		else if (field.type === "CHOICE" && !isFormAnswerMissing(answer)) displayAnswer = "Selected option was removed from the template"
		else if (field.type === "SCALE" && typeof answer === "string" && !isFormAnswerMissing(answer)) displayAnswer = "Selected special answer was removed from the template"
		const conditionValue = field.showWhen ? answers[field.showWhen.fieldId] : null
		const isRelevant = !field.showWhen || (Number.isInteger(Number(conditionValue)) && conditionValue !== "" && conditionValue != null && Number(conditionValue) <= Number(field.showWhen.atMost))
		section.fields.push({...field, answer, hasAnswer: !isFormAnswerMissing(answer), displayAnswer, isRelevant})
	}
	const completion = getFormCompletion(config, answers)
	return {sections, completion: {...completion, minimumGroups: completion.minimumGroups || [], incompleteGroups: completion.incompleteGroups || []}}
}

export function getFormPayloadErrors(payload = {}, config = {}) {
	if (!payload.form) return ["Invalid engine payload"]
	const errors = getFormConfigErrors(payload.form)
	if (errors.length) return errors
	if (config.requireFocus && !String(config.focus ?? "").trim()) return ["Change focus is required"]
	if (config.rejectEmptyFormSubmission && !(config.fields || config.formElements || []).length) return ["Add a question before submitting"]
	if (config.discreteRating) {
		const field = (config.fields || []).find(item => item.id === config.discreteRating.fieldId)
		const options = field?.answerOptions || []
		if (!field || field.type !== "SCALE" || !options.length) return ["Configure a rating question and choices before submitting"]
		if (payload.completionStatus === "unable") return payload.answers?.[field.id] == null || payload.answers?.[field.id] === "" ? [] : ["Unable-to-complete response cannot include a rating"]
		if (payload.completionStatus !== "rated" || !options.some(option => option.id === payload.answers?.[field.id])) return ["Choose one configured rating or mark unable to complete"]
	}
	if (config.requirePairedRatingAndTextFields) {
		const fields = config.fields || config.formElements || []
		if (!fields.some(field => field.id === config.ratingFieldId && field.type === "SCALE") || !fields.some(field => field.id === config.reasonFieldId && ["TEXT", "MULTILINE"].includes(field.type))) return ["Configure an importance rating and reasons question before submitting"]
	}
	if (config.requireAnyAnswer && !(config.fields || config.formElements || []).some(field => payload.answers?.[field.id] !== null && payload.answers?.[field.id] !== undefined && String(payload.answers?.[field.id]).trim() !== "")) return ["Enter a response before submitting"]
	if (config.requireAnyTextAnswer && !(config.fields || []).some(field => typeof payload.answers?.[field.id] === "string" && payload.answers[field.id].trim())) {
		return ["Enter a response before submitting"]
	}
	const requiredErrors = getRequiredFormAnswerErrors(payload, config)
	if (requiredErrors.length) return requiredErrors
	if (config.rejectBlankRequiredText && (config.fields || config.formElements || []).some(field => field.isRequired && ["TEXT", "MULTILINE"].includes(field.type) && !String(payload.answers?.[field.id] ?? "").trim())) return ["Required answer is missing"]
	for (const field of config.fields || config.formElements || []) {
		const selectedSpecial = (field.specialValues || []).find(option => option.id === payload.answers?.[field.id])
		if (selectedSpecial?.requiresFieldId && isFormAnswerMissing(payload.answers?.[selectedSpecial.requiresFieldId])) return ["Explanation is required for the selected answer"]
		if (field.type === "CHOICE") {
			const answer = payload.answers?.[field.id]
			if (!isFormAnswerMissing(answer) && !(field.choices || []).some(choice => choice.id === answer)) return ["Invalid choice answer"]
		}
		if (field.type !== "SCALE") continue
		const answer = payload.answers?.[field.id]
		if (answer === "" || answer == null) continue
		if ((field.specialValues || []).some(option => option.id === answer)) continue
		if (field.answerOptions !== undefined) {
			if (!field.answerOptions.some(option => option.id === answer)) return ["Unknown rating choice"]
			continue
		}
		const isNumericAnswer = typeof answer === "number" || (typeof answer === "string" && !!answer.trim())
		if (!isNumericAnswer) return ["Invalid rating answer"]
		const value = Number(answer)
		if (!Number.isInteger(value) || value < Number(field.min ?? 0) || value > Number(field.max ?? 10)) return ["Rating answer is outside its configured range"]
	}
	return []
}

export function createFormSummaryInput(payload = {}, config = {}) {
	const answers = payload.answers || {}
	let headline = ""
	const fields = config.fields || config.formElements || []
	const preferred = fields.find(field => field.id === config.headlineFieldId)
	const orderedFields = preferred ? [preferred, ...fields.filter(field => field.id !== preferred.id)] : fields
	for (const field of orderedFields) {
		const answer = answers[field.id]
		if (answer !== "" && answer != null) {
			const choice = field.answerOptions?.find(option => option.id === answer)
			headline = `${field.title || ""}: ${choice ? `${choice.value} · ${choice.title}` : Array.isArray(answer) ? answer.join(", ") : answer}`
			break
		}
	}
	const summary = {headline: config.discreteRating && payload.completionStatus === "unable" ? "Unable to complete" : headline, form: JSON.parse(JSON.stringify(payload.form || {})), answers, scoreResult: payload.scoreResult ?? null}
	if (config.discreteRating) {
		summary.completionStatus = payload.completionStatus || "unanswered"
		summary.administrationDate = payload.administrationDate || ""
	}
	if (config.trackCompletion || (config.fields || config.formElements || []).some(field => field.isCompletionRequired)) {
		summary.completion = getFormCompletion(config, answers)
	}
	return summary
}
